
import React, { useState, useEffect, useCallback } from 'react';
import { TicketPriority, YesNoUnknown, Employee } from '../types';
import { Button } from './Button';
import type { NewTicketFormData } from '../App';
import { GoogleGenAI } from "@google/genai";

// Helper to get API key
const getApiKey = () => {
  if (typeof process === 'undefined' || !process.env || !process.env.API_KEY) {
    console.warn("API_KEY not found in process.env. AI Suggestions will be disabled.");
    return undefined; 
  }
  return process.env.API_KEY;
};

const initialFormDataInternal = {
  customer: '',
  customerPhoneNumber: '',
  customerEmail: '',
  deviceModel: '',
  deviceSerialNumber: '',
  subject: '',
  description: '',
  priority: TicketPriority.MEDIUM,
  devicePasscode: '',
  dataBackupStatus: YesNoUnknown.UNKNOWN,
  findMyDeviceDisabled: YesNoUnknown.UNKNOWN,
  amountDue: 0,
  referralSource: '',
  category: '', // Added category
  estimateDetails: '', // Ensure this is part of the initial form data if used
};

interface AISuggestion {
  category: string;
  priority: string; // Gemini will return string like "High", "Medium"
  error?: string;
}

// Helper to convert string priority from AI to enum
const convertAIPriorityToEnum = (priorityString: string): TicketPriority | undefined => {
    const upperPriority = priorityString.toUpperCase();
    if (upperPriority === TicketPriority.HIGH.toUpperCase()) return TicketPriority.HIGH;
    if (upperPriority === TicketPriority.MEDIUM.toUpperCase()) return TicketPriority.MEDIUM;
    if (upperPriority === TicketPriority.LOW.toUpperCase()) return TicketPriority.LOW;
    return undefined;
};

interface NewTicketModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (formData: NewTicketFormData) => string; // Changed from void to string
  currentUser: Employee | null;
}

export const NewTicketModal: React.FC<NewTicketModalProps> = ({ isOpen, onClose, onSave, currentUser }) => {
  const [formData, setFormData] = useState(initialFormDataInternal);
  const [errors, setErrors] = useState<Partial<Record<keyof typeof initialFormDataInternal, string>>>({});
  
  const [isAISuggesting, setIsAISuggesting] = useState(false);
  const [aiSuggestion, setAiSuggestion] = useState<AISuggestion | null>(null);
  const apiKey = getApiKey();


  useEffect(() => {
    if (isOpen) {
      setFormData(initialFormDataInternal); 
      setErrors({});
      setAiSuggestion(null);
      setIsAISuggesting(false);
    }
  }, [isOpen]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({ 
      ...prev, 
      [name]: type === 'number' ? parseFloat(value) : value 
    }));
    if (errors[name as keyof typeof initialFormDataInternal]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof typeof initialFormDataInternal, string>> = {};
    if (!formData.customer.trim()) newErrors.customer = 'Customer name is required.';
    if (!formData.subject.trim()) newErrors.subject = 'Issue with device is required.';
    if (formData.customerEmail && !/^\S+@\S+\.\S+$/.test(formData.customerEmail)) {
      newErrors.customerEmail = 'Invalid email format.';
    }
    // Ensure amountDue is validated correctly as a number if it's being parsed
    if (formData.amountDue && isNaN(Number(formData.amountDue))) {
        newErrors.amountDue = 'Amount must be a number.';
    } else if (formData.amountDue && Number(formData.amountDue) < 0) {
        newErrors.amountDue = 'Amount cannot be negative.';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      const dataToSave: NewTicketFormData = {
        ...formData,
        amountDue: Number(formData.amountDue) || 0, // Ensure amountDue is a number
        aiRawSuggestion: aiSuggestion && !aiSuggestion.error ? { category: aiSuggestion.category, priorityString: aiSuggestion.priority } : undefined,
      };
      onSave(dataToSave);
    }
  };

  const fetchAISuggestions = useCallback(async () => {
    if (!formData.subject.trim() && !formData.description.trim()) {
      setAiSuggestion({ category: '', priority: '', error: "Please provide a subject or description for AI suggestions." });
      return;
    }
    if (!apiKey) {
      setAiSuggestion({ category: '', priority: '', error: "AI suggestions disabled: API key not configured." });
      return;
    }

    setIsAISuggesting(true);
    setAiSuggestion(null);

    try {
      const ai = new GoogleGenAI({ apiKey });
      const prompt = `Analyze the following ticket. Suggest a concise 'category' (e.g., 'Screen Repair', 'Battery Issue', 'Software Problem', 'Data Recovery', 'Connectivity') and 'priority' ('Low', 'Medium', 'High'). Respond strictly in JSON format: {"category": "Suggested Category", "priority": "Suggested Priority"}. Ticket Subject: "${formData.subject}". Description: "${formData.description}".`;
      
      const response = await ai.models.generateContent({
        model: "gemini-2.5-flash-preview-04-17",
        contents: prompt,
        config: { responseMimeType: "application/json" }
      });

      let jsonStr = response.text.trim();
      const fenceRegex = /^```(\w*)?\s*\n?(.*?)\n?\s*```$/s;
      const match = jsonStr.match(fenceRegex);
      if (match && match[2]) {
        jsonStr = match[2].trim();
      }
      
      const parsed = JSON.parse(jsonStr) as { category: string; priority: string };
      if (parsed.category && parsed.priority) {
        setAiSuggestion({ category: parsed.category, priority: parsed.priority });
      } else {
        setAiSuggestion({ category: '', priority: '', error: "AI returned an unexpected format." });
      }

    } catch (error) {
      console.error("Error fetching AI suggestions:", error);
      setAiSuggestion({ category: '', priority: '', error: "Failed to get AI suggestions. Please try again." });
    } finally {
      setIsAISuggesting(false);
    }
  }, [formData.subject, formData.description, apiKey]);
  
  const applyAISuggestedCategory = () => {
    if (aiSuggestion && aiSuggestion.category && !aiSuggestion.error) {
      setFormData(prev => ({ ...prev, category: aiSuggestion.category }));
    }
  };

  const applyAISuggestedPriority = () => {
    if (aiSuggestion && aiSuggestion.priority && !aiSuggestion.error) {
      const enumPriority = convertAIPriorityToEnum(aiSuggestion.priority);
      if (enumPriority) {
        setFormData(prev => ({ ...prev, priority: enumPriority }));
      }
    }
  };


  if (!isOpen) return null;

  const renderError = (field: keyof typeof initialFormDataInternal) => {
    return errors[field] ? <p className="text-red-500 text-xs mt-1">{errors[field]}</p> : null;
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[100]"
      aria-labelledby="new-ticket-modal-title"
      role="dialog"
      aria-modal="true"
    >
      <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 id="new-ticket-modal-title" className="text-xl font-semibold text-slate-800">
            Create New Service Request
          </h2>
          <button
            onClick={onClose}
            className="text-slate-500 hover:text-slate-700"
            aria-label="Close modal"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Customer and Device Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="customer" className="block text-sm font-medium text-slate-700">Customer Full Name <span className="text-red-500">*</span></label>
              <input type="text" name="customer" id="customer" value={formData.customer} onChange={handleChange} className="mt-1 block w-full form-input" aria-required="true" />
              {renderError('customer')}
            </div>
            <div>
              <label htmlFor="customerPhoneNumber" className="block text-sm font-medium text-slate-700">Phone Number</label>
              <input type="tel" name="customerPhoneNumber" id="customerPhoneNumber" value={formData.customerPhoneNumber} onChange={handleChange} className="mt-1 block w-full form-input" />
            </div>
          </div>
          <div>
            <label htmlFor="customerEmail" className="block text-sm font-medium text-slate-700">Email Address</label>
            <input type="email" name="customerEmail" id="customerEmail" value={formData.customerEmail} onChange={handleChange} className="mt-1 block w-full form-input" />
            {renderError('customerEmail')}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="deviceModel" className="block text-sm font-medium text-slate-700">Device Model</label>
              <input type="text" name="deviceModel" id="deviceModel" value={formData.deviceModel} onChange={handleChange} className="mt-1 block w-full form-input" />
            </div>
            <div>
              <label htmlFor="deviceSerialNumber" className="block text-sm font-medium text-slate-700">Device Serial Number</label>
              <input type="text" name="deviceSerialNumber" id="deviceSerialNumber" value={formData.deviceSerialNumber} onChange={handleChange} className="mt-1 block w-full form-input" />
            </div>
          </div>
          
          {/* Issue Details */}
          <div>
            <label htmlFor="subject" className="block text-sm font-medium text-slate-700">Issue with Device (Subject) <span className="text-red-500">*</span></label>
            <input type="text" name="subject" id="subject" value={formData.subject} onChange={handleChange} className="mt-1 block w-full form-input" aria-required="true" />
            {renderError('subject')}
          </div>
          <div>
            <label htmlFor="description" className="block text-sm font-medium text-slate-700">Additional Details About Repair</label>
            <textarea name="description" id="description" rows={3} value={formData.description} onChange={handleChange} className="mt-1 block w-full form-input"></textarea>
          </div>

          {/* AI Suggestions Section */}
          <div className="mt-4 p-4 bg-slate-50 rounded-lg border border-slate-200">
            <div className="flex justify-between items-center mb-2">
              <h4 className="text-md font-semibold text-slate-700">AI Suggestions</h4>
              <Button
                type="button"
                onClick={fetchAISuggestions}
                variant="ghost"
                className="text-xs !py-1 !px-2 !text-[var(--color-primary)] hover:!bg-[var(--color-primary)] hover:!bg-opacity-10"
                disabled={isAISuggesting || !apiKey}
              >
                {isAISuggesting ? 'Getting Suggestions...' : 'Get AI Suggestions'}
              </Button>
            </div>
            {isAISuggesting && (
              <div className="text-sm text-slate-600">Loading suggestions...</div>
            )}
            {aiSuggestion && !isAISuggesting && (
              <div className="space-y-2 text-sm">
                {aiSuggestion.error && <p className="text-red-600">{aiSuggestion.error}</p>}
                {aiSuggestion.category && !aiSuggestion.error && (
                  <div className="flex justify-between items-center">
                    <p>Category: <span className="font-semibold">{aiSuggestion.category}</span></p>
                    <Button
                      type="button"
                      onClick={applyAISuggestedCategory}
                      variant="ghost"
                      className="text-xs !py-1 !px-2 !text-[var(--color-primary)] hover:!bg-[var(--color-primary)] hover:!bg-opacity-10"
                      disabled={!aiSuggestion || !aiSuggestion.category || !!aiSuggestion.error}
                    >
                      Apply Category
                    </Button>
                  </div>
                )}
                {aiSuggestion.priority && !aiSuggestion.error && (
                  <div className="flex justify-between items-center">
                    <p>Priority: <span className="font-semibold">{aiSuggestion.priority}</span></p>
                    <Button 
                      type="button"
                      onClick={applyAISuggestedPriority}
                      variant="ghost"
                      className="text-xs !py-1 !px-2 !text-[var(--color-primary)] hover:!bg-[var(--color-primary)] hover:!bg-opacity-10"
                      disabled={!aiSuggestion || !aiSuggestion.priority || !!aiSuggestion.error}
                    >
                      Apply Priority
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>
          
          {/* Ticket Configuration Options */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="priority" className="block text-sm font-medium text-slate-700">Priority</label>
              <select name="priority" id="priority" value={formData.priority} onChange={handleChange} className="mt-1 block w-full form-input">
                {Object.values(TicketPriority).map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <div> {/* This is the category input */}
              <label htmlFor="category" className="block text-sm font-medium text-slate-700">Category</label>
              <input type="text" name="category" id="category" value={formData.category} onChange={handleChange} className="mt-1 block w-full form-input" placeholder="e.g., Screen Repair"/>
            </div>
          </div>
          <div>
            <label htmlFor="amountDue" className="block text-sm font-medium text-slate-700">Initial Quote Amount Due (optional)</label>
            <input type="number" name="amountDue" id="amountDue" value={formData.amountDue} onChange={handleChange} step="0.01" className="mt-1 block w-full form-input" placeholder="e.g. 75.00"/>
            {renderError('amountDue')}
          </div>
          <div>
            <label htmlFor="estimateDetails" className="block text-sm font-medium text-slate-700">Estimate Details / Scope of Work (optional)</label>
            <textarea name="estimateDetails" id="estimateDetails" value={formData.estimateDetails || ''} onChange={handleChange} rows={2} className="mt-1 block w-full form-input" placeholder="e.g., Screen replacement, diagnostic fee..."></textarea>
          </div>
          
          <fieldset className="mt-4 border-t border-slate-200 pt-4">
            <legend className="text-sm font-medium text-slate-700 mb-1">Additional Device Info (Internal Use)</legend>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label htmlFor="devicePasscode" className="block text-xs font-medium text-slate-600">Device Passcode</label>
                  <input type="text" name="devicePasscode" id="devicePasscode" value={formData.devicePasscode} onChange={handleChange} className="mt-1 block w-full form-input text-xs" />
                </div>
                <div>
                  <label htmlFor="dataBackupStatus" className="block text-xs font-medium text-slate-600">Data Backup?</label>
                  <select name="dataBackupStatus" id="dataBackupStatus" value={formData.dataBackupStatus} onChange={handleChange} className="mt-1 block w-full form-input text-xs">
                    {Object.values(YesNoUnknown).map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <label htmlFor="findMyDeviceDisabled" className="block text-xs font-medium text-slate-600">Find My Disabled?</label>
                  <select name="findMyDeviceDisabled" id="findMyDeviceDisabled" value={formData.findMyDeviceDisabled} onChange={handleChange} className="mt-1 block w-full form-input text-xs">
                    {Object.values(YesNoUnknown).map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
            </div>
            <div>
                <label htmlFor="referralSource" className="block text-xs font-medium text-slate-600 mt-2">Referral Source</label>
                <input type="text" name="referralSource" id="referralSource" value={formData.referralSource} onChange={handleChange} className="mt-1 block w-full form-input text-xs" placeholder="e.g., Google, Friend..." />
            </div>
          </fieldset>

          <div className="border-t border-slate-200 pt-4 flex justify-end space-x-3">
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" variant="primary">
              Create Ticket
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};